import React from 'react'
import { ChevronsUpDown, SkipForward, SkipBack } from 'lucide-react'
import type { Podcast } from '../../types/podcast'
import EditorAudioPlayer, { type EditorAudioPlayerHandle } from '../../features/podcast-editor/components/footer/editor-audio-player'
import Popover from '../ui/popover'
import { Tooltip } from '../ui/tooltip'
import { downloadFile, sanitizeFilename } from '../../lib/file-download'
import { track } from '../../lib/analytics/posthog'
import { markPodcastPlayedForCurrentUser } from '../../api/podcasts'
import {
	SERIES_EPISODE_ENDED_EVENT,
	SERIES_PLAY_EPISODE_EVENT,
	SERIES_TOGGLE_EPISODE_EVENT,
	SERIES_EPISODE_PLAY_STATE_EVENT,
	type SeriesEpisodePlayStateDetail,
} from '../../lib/series-feedback-events'

type StickyEpisodePlayerProps = {
	episodes: Podcast[]
	seriesId?: string | null
	className?: string
		anchorRef?: React.RefObject<HTMLElement | null>
}

// Sticky player that stays at the bottom of the content column and plays a queue of episodes
// Reuses the existing EditorAudioPlayer to inherit all playback/localStorage/analytics behavior
const StickyEpisodePlayer: React.FC<StickyEpisodePlayerProps> = ({ episodes, seriesId, className, anchorRef }) => {
	// Build queue: only Completed episodes that have an audio file, sorted oldest -> newest (chronological)
	const queue = React.useMemo(() => {
		const list = (episodes || []).filter(e => !!e.audioFileUrl && !!e.id)
		return list.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
	}, [episodes])

		// Persist the selected episode robustly by id (stable across reordering/new episodes) with index as fallback
		const indexKey = React.useMemo(() => seriesId ? `pg.series.${seriesId}.stickyPlayer.index` : `pg.stickyPlayer.index`, [seriesId])
		const idKey = React.useMemo(() => seriesId ? `pg.series.${seriesId}.stickyPlayer.episodeId` : `pg.stickyPlayer.episodeId`, [seriesId])

		const [index, setIndex] = React.useState<number>(0)
		const [autoPlaySignal, setAutoPlaySignal] = React.useState<boolean>(false)
		const playerRef = React.useRef<EditorAudioPlayerHandle | null>(null)

		// Listen to external play triggers
		React.useEffect(() => {
			const handlePlayEpisode = (e: CustomEvent<{ episodeId: string }>) => {
				const epId = e.detail.episodeId;
				const idx = queue.findIndex(ep => ep.id === epId);
				if (idx >= 0) {
					if (idx === index) {
						// Same episode: just resume/play without re-mount
						playerRef.current?.play()
					} else {
						setIndex(idx);
						setAutoPlaySignal(true);
					}
				}
			};
			const handleToggleEpisode = (e: CustomEvent<{ episodeId: string }>) => {
				const epId = e.detail.episodeId
				const idx = queue.findIndex(ep => ep.id === epId)
				if (idx < 0) return
				if (idx === index) {
					playerRef.current?.togglePlay()
				} else {
					setIndex(idx)
					setAutoPlaySignal(true)
				}
			}
			window.addEventListener(SERIES_PLAY_EPISODE_EVENT, handlePlayEpisode as EventListener);
			window.addEventListener(SERIES_TOGGLE_EPISODE_EVENT, handleToggleEpisode as EventListener);
			return () => {
				window.removeEventListener(SERIES_PLAY_EPISODE_EVENT, handlePlayEpisode as EventListener);
				window.removeEventListener(SERIES_TOGGLE_EPISODE_EVENT, handleToggleEpisode as EventListener);
			}
		}, [queue, index]);

		// On mount or when queue updates, restore selection from localStorage (prefer episode id)
		React.useEffect(() => {
			if (typeof window === 'undefined' || queue.length === 0) return
			const savedId = window.localStorage.getItem(idKey)
			if (savedId) {
				const byId = queue.findIndex(ep => ep.id === savedId)
				if (byId >= 0) { setIndex(byId); return }
			}
			const rawIndex = window.localStorage.getItem(indexKey)
			const n = rawIndex ? parseInt(rawIndex, 10) : 0
			setIndex(Number.isFinite(n) ? Math.min(Math.max(0, n), queue.length - 1) : 0)
		}, [queue, idKey, indexKey])

		// Save whenever selection changes
		React.useEffect(() => {
			if (typeof window === 'undefined' || queue.length === 0) return
			const current = queue[Math.min(Math.max(0, index), queue.length - 1)]
			window.localStorage.setItem(indexKey, String(index))
			if (current?.id) window.localStorage.setItem(idKey, current.id)
		}, [index, queue, indexKey, idKey])

	// Clamp index if queue changes
	React.useEffect(() => {
		if (!queue.length) return
		if (index < 0) setIndex(0)
		if (index > queue.length - 1) setIndex(queue.length - 1)
	}, [queue.length, index])

	const current = queue[index]

	const canPrev = index > 0
	const canNext = index < queue.length - 1

	const playPrev = () => { if (canPrev) setIndex(i => Math.max(0, i - 1)) }
	const playNext = () => { if (canNext) setIndex(i => Math.min(queue.length - 1, i + 1)) }
	const handleEnded = () => {
		window.dispatchEvent(new CustomEvent(SERIES_EPISODE_ENDED_EVENT, {
			detail: {
				seriesId,
				episodeId: current.id,
			},
		}))
		playNext()
	}

	const [pickerOpen, setPickerOpen] = React.useState(false)
	const [progressRatio, setProgressRatio] = React.useState(0)

	React.useEffect(() => { setProgressRatio(0) }, [current?.id])

	const handleTimeUpdate = React.useCallback((time: number, total: number) => {
		if (!total || !isFinite(total) || total <= 0) return
		setProgressRatio(Math.min(Math.max(0, time / total), 1))
	}, [])

	const currentIdRef = React.useRef<string | null>(null)
	React.useEffect(() => { currentIdRef.current = current?.id ?? null }, [current?.id])

	const handlePlayStateChange = React.useCallback((isPlaying: boolean) => {
		const detail: SeriesEpisodePlayStateDetail = { episodeId: currentIdRef.current, isPlaying }
		window.dispatchEvent(new CustomEvent(SERIES_EPISODE_PLAY_STATE_EVENT, { detail }))
	}, [])

	// When the current episode changes, signal that previous episode is no longer playing
	React.useEffect(() => {
		if (!current?.id) return
		const detail: SeriesEpisodePlayStateDetail = { episodeId: current.id, isPlaying: false }
		window.dispatchEvent(new CustomEvent(SERIES_EPISODE_PLAY_STATE_EVENT, { detail }))
	}, [current?.id])

	const handleDownload = React.useCallback(() => {
		if (!current?.audioFileUrl || !current?.title) return
		const safe = sanitizeFilename(current.title)
		const filename = `${safe}.mp3`
		void downloadFile(current.audioFileUrl, filename).then((ok) => {
			if (ok) {
				track('podcast_downloaded')
				void markPodcastPlayedForCurrentUser(current.id).catch(() => { /* noop */ })
			}
		})
	}, [current?.audioFileUrl, current?.title, current?.id])

		// Layout: compute left/width so the player aligns with the content column and does not overlap the menu
		const [fixedStyle, setFixedStyle] = React.useState<React.CSSProperties>({})
		React.useLayoutEffect(() => {
			const el = anchorRef?.current || document.querySelector('[data-sticky-anchor]') as HTMLElement | null
			if (!el) return
			const update = () => {
				const rect = el.getBoundingClientRect()
				setFixedStyle({ left: `${rect.left + window.scrollX}px`, width: `${rect.width}px` })
			}
			update()
			const ro = 'ResizeObserver' in window ? new ResizeObserver(update) : null
			ro?.observe(el)
			window.addEventListener('resize', update)
			window.addEventListener('scroll', update, { passive: true })
			return () => {
				ro?.disconnect()
				window.removeEventListener('resize', update)
				window.removeEventListener('scroll', update as any)
			}
		}, [anchorRef])

		if (!queue.length || !current) return null

	return (
			<div
				className={"fixed bottom-0 z-30 border-t border-black/10 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 sm:rounded-t-2xl overflow-hidden " + (className || '')}
				style={{ ...fixedStyle }}
			>
			{/* Thin progress bar across the top (Spotify/YT-Music style) — visible on all sizes on mobile, top edge only */}
			<div className="sm:hidden absolute inset-x-0 top-0 h-0.5 bg-foreground/10 pointer-events-none">
				<div
					className="h-full bg-brand-600 transition-[width] duration-150 ease-linear"
					style={{ width: `${progressRatio * 100}%` }}
				/>
			</div>

			{/* MOBILE LAYOUT: title + queue controls + play (no picker / no download / no speed) */}
			<div className="sm:hidden px-3 pt-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))]">
				<div className="flex items-center gap-2 min-w-0">
					<div className="min-w-0 flex-1">
						<div className="text-[10px] uppercase tracking-wide text-foreground/50 font-medium">Now playing</div>
						<div className="text-sm font-semibold text-foreground truncate" title={current.title}>{current.title}</div>
					</div>
					<button
						type="button"
						onClick={playPrev}
						disabled={!canPrev}
						className="h-9 w-9 shrink-0 rounded-full flex items-center justify-center text-foreground/70 hover:text-foreground hover:bg-black/5 active:bg-black/10 disabled:opacity-30 disabled:cursor-not-allowed transition cursor-pointer"
						aria-label="Previous episode"
					>
						<SkipBack className="h-5 w-5" />
					</button>
					{current.audioFileUrl && (
						<EditorAudioPlayer
							ref={playerRef}
							audioUrl={current.audioFileUrl}
							durationSeconds={current.durationSeconds}
							onDownload={handleDownload}
							podcastId={current.id}
							onEnded={handleEnded}
							autoPlay={autoPlaySignal}
							onTimeUpdate={handleTimeUpdate}
							onPlayStateChange={handlePlayStateChange}
							className="!w-auto !p-0 !gap-0 !bg-transparent !border-0"
							compact
						/>
					)}
					<button
						type="button"
						onClick={playNext}
						disabled={!canNext}
						className="h-9 w-9 shrink-0 rounded-full flex items-center justify-center text-foreground/70 hover:text-foreground hover:bg-black/5 active:bg-black/10 disabled:opacity-30 disabled:cursor-not-allowed transition cursor-pointer"
						aria-label="Next episode"
					>
						<SkipForward className="h-5 w-5" />
					</button>
				</div>
			</div>

			{/* DESKTOP LAYOUT: full picker + reused player */}
			<div className="hidden sm:block px-4 md:px-6 py-3">
				<div className="flex items-center gap-3">
					{/* Queue controls */}
					<div className="hidden sm:flex items-center gap-1">
						<Tooltip content={canPrev ? 'Previous episode' : 'No previous episode'}>
							<button
								type="button"
								onClick={playPrev}
								disabled={!canPrev}
								className="h-9 w-9 rounded-full flex items-center justify-center text-foreground/60 hover:text-foreground hover:bg-black/5 active:bg-black/10 disabled:opacity-40 disabled:cursor-not-allowed transition cursor-pointer"
								aria-label="Previous episode"
							>
								<SkipBack className="h-4 w-4" />
							</button>
						</Tooltip>
						<Tooltip content={canNext ? 'Next episode' : 'No next episode'}>
							<button
								type="button"
								onClick={playNext}
								disabled={!canNext}
								className="h-9 w-9 rounded-full flex items-center justify-center text-foreground/60 hover:text-foreground hover:bg-black/5 active:bg-black/10 disabled:opacity-40 disabled:cursor-not-allowed transition cursor-pointer"
								aria-label="Next episode"
							>
								<SkipForward className="h-4 w-4" />
							</button>
						</Tooltip>
					</div>

					{/* Episode picker */}
					<Popover
						open={pickerOpen}
						onOpenChange={setPickerOpen}
						side="top"
						align="start"
						className="min-w-[260px] max-w-[80vw] p-1"
						trigger={({ ref, onClick, 'aria-expanded': expanded }) => (
							<button
								type="button"
								ref={ref as any}
								onClick={onClick}
								aria-expanded={expanded}
								className="group inline-flex items-center gap-2 rounded-xl border border-black/10 bg-white/70 hover:bg-white/90 active:bg-white px-3 h-10 text-left cursor-pointer min-w-0"
								title={current.title}
							>
								<div className="min-w-0">
									<div className="text-xs text-foreground/60">Now playing</div>
									<div className="text-sm font-medium text-foreground truncate" title={current.title}>{current.title}</div>
								</div>
								<ChevronsUpDown className="h-4 w-4 text-foreground/50 ml-1 flex-shrink-0" />
							</button>
						)}
					>
						<div className="max-h-[40vh] overflow-auto">
							{queue.map((ep, i) => (
								<button
									key={ep.id}
									onClick={() => { setIndex(i); setPickerOpen(false) }}
									className={`w-full text-left rounded-lg px-2.5 py-2 text-sm cursor-pointer transition flex items-center gap-2 ${i === index ? 'bg-brand-50 text-brand-700' : 'text-foreground/80 hover:bg-black/5 hover:text-foreground'}`}
								>
									<span className="inline-flex h-5 w-5 items-center justify-center rounded-md text-[11px] font-medium bg-foreground/10 text-foreground/60 flex-shrink-0">{i + 1}</span>
									<span className="truncate" title={ep.title}>{ep.title}</span>
								</button>
							))}
						</div>
					</Popover>

					{/* Core player (reused) */}
					<div className="flex-1 min-w-0 flex justify-end sm:justify-start">
						{current.audioFileUrl && (
							<EditorAudioPlayer
								ref={playerRef}
								audioUrl={current.audioFileUrl}
								durationSeconds={current.durationSeconds}
								onDownload={handleDownload}
								podcastId={current.id}
								onEnded={handleEnded}
								autoPlay={autoPlaySignal}
								onTimeUpdate={handleTimeUpdate}
								onPlayStateChange={handlePlayStateChange}
							/>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}

export default StickyEpisodePlayer

